'use client';
import { useEffect } from 'react';
import { Env } from '@/libs/env';
import { safeCaptureExceptionFull } from '@/utils/sentry-utils';
import { CHUNK_LOAD_ERROR_SESSION_KEY } from '@/utils/chunk-load-error';

export default function RscVersionFix() {
  useEffect(() => {
    const original = window.fetch;
    const release = Env.RELEASE || process.env.NEXT_PUBLIC_GIT_COMMIT || 'dev-local';

    // 判断是否为开发环境或默认版本号
    // 如果是唯一的 git commit hash（长度 >= 8），则不需要 no-store
    // 如果是 'dev-local' 或开发环境，则需要 no-store 避免缓存问题
    const isDevOrDefaultRelease =
      Env.NODE_ENV === 'development' ||
      release === 'dev-local' ||
      release.length < 8;

    // 读取 chunk 加载异常标记，若有则给 RSC 请求加随机参数强制刷新
    let chunkErrorCacheBust: string | null = null;
    try {
      if (sessionStorage.getItem(CHUNK_LOAD_ERROR_SESSION_KEY) === '1') {
        chunkErrorCacheBust = String(Date.now());
      }
    } catch {
      // sessionStorage 不可用时忽略
    }

    window.fetch = function (resource, config) {
      // 获取请求 URL（处理字符串、URL 对象和 Request 对象三种情况）
      let url: URL | null = null;
      let requestUrl: string | null = null;

      if (typeof resource === 'string') {
        requestUrl = resource;
      } else if (resource instanceof URL) {
        // 如果是 URL 对象，直接使用
        url = resource;
        requestUrl = resource.href;
      } else if (resource instanceof Request) {
        requestUrl = resource.url;
      }

      // 检查是否为 RSC 请求
      // Next.js RSC 请求通常包含 _rsc 参数或 .txt 扩展名
      // 排除 robots.txt（它虽然是 .txt 文件，但不是 RSC 请求）
      const isRscRequest = requestUrl && (
        requestUrl.includes('_rsc') ||
        (requestUrl.includes('.txt') && !requestUrl.includes('/robots.txt')) ||
        requestUrl.includes('?rsc=') ||
        requestUrl.includes('&rsc=')
      );

      // 判断是否为测试域名（已注释，不再使用）
      // const isTestDomain = typeof window !== 'undefined' &&
      //   window.location.hostname === 'web-test.cggame123.com';

      if (isRscRequest) {
        try {
          // 如果还没有 URL 对象，创建一个
          if (!url) {
            if (requestUrl) {
              // 处理完整 URL 和相对路径两种情况
              url = requestUrl.startsWith('http://') || requestUrl.startsWith('https://')
                ? new URL(requestUrl)
                : new URL(requestUrl, location.origin);
            }
          }

          if (url) {
            // 统一逻辑：保持在源站，但插入版本号到文件名
            if (url.pathname.endsWith('.txt')) {
              // 在文件名中插入版本号（例如：affiliate.txt -> affiliate_cacd790e.txt）
              const pathParts = url.pathname.split('/');
              const fileName = pathParts[pathParts.length - 1];

              if (fileName.endsWith('.txt')) {
                const nameWithoutExt = fileName.slice(0, -4); // 移除 .txt
                const newFileName = `${nameWithoutExt}_${release.slice(0, 8)}.txt`; // 插入版本号
                pathParts[pathParts.length - 1] = newFileName;
                url.pathname = pathParts.join('/');
              }
              // 保持在源站，不修改 hostname
            } else {
              // 普通环境：在 rsc 参数之外再加版本号
              // http://localhost:3000/en?menuTab=Sports&_rsc=9xv77 -> http://localhost:3000/en?menuTab=Sports&_rsc=9xv77&v=build-123
              url.searchParams.set('v', release);
              // 若本 session 有 chunk 加载异常，加随机参数强制绕过 CDN/浏览器缓存
              if (chunkErrorCacheBust) {
                url.searchParams.set('_cb', chunkErrorCacheBust);
              }
            }

            // 修复多语言路径重复：
            // /en/en_xxx -> /en_xxx（文件名前缀重复，下划线形式）
            // /en/en/xxx -> /en/xxx（路径段重复，斜杠形式）
            // 适用于所有 locale：en、hi、pt 等
            const pathParts = url.pathname.split('/').filter(Boolean);
            if (
              pathParts.length >= 2 &&
              (pathParts[1].startsWith(pathParts[0] + '_') || pathParts[0] === pathParts[1])
            ) {
              url.pathname = '/' + pathParts.slice(1).join('/');
            }

            const finalUrl = url.toString();
            const startTime = Date.now();

            // 包装 fetch 调用，添加错误和响应状态监控
            const wrappedFetch = (() => {
              // 如果是字符串，直接返回新 URL
              if (typeof resource === 'string') {
                return original.call(this, finalUrl, {
                  ...config,
                  // 仅在开发环境或默认版本号时使用 no-store
                  // 生产环境的唯一版本号已经能保证 URL 不同，无需禁用缓存
                  ...(isDevOrDefaultRelease && { cache: 'no-store' })
                });
              }

              // 如果是 URL 对象，使用修改后的 href
              if (resource instanceof URL) {
                return original.call(this, finalUrl, {
                  ...config,
                  ...(isDevOrDefaultRelease && { cache: 'no-store' })
                });
              }

              // 如果是 Request 对象，创建新的 Request
              if (resource instanceof Request) {
                const newRequest = new Request(finalUrl, {
                  method: resource.method,
                  headers: resource.headers,
                  body: resource.body,
                  referrer: resource.referrer,
                  referrerPolicy: resource.referrerPolicy,
                  mode: resource.mode,
                  credentials: resource.credentials,
                  cache: isDevOrDefaultRelease ? 'no-store' : resource.cache,
                  redirect: resource.redirect,
                  integrity: resource.integrity,
                  keepalive: resource.keepalive,
                  signal: resource.signal,
                  ...config,
                });
                return original.call(this, newRequest, config);
              }

              return original.call(this, resource, config);
            })();

            // 返回包装后的 Promise,监控响应状态和错误
            return wrappedFetch
              .then(response => {
                const duration = Date.now() - startTime;

                // 检查响应状态
                if (!response.ok) {
                  const errorMessage = `RSC request failed with status ${response.status}`;

                  console.warn(
                    '[RscVersionFix] RSC request failed',
                    '\n  URL:', finalUrl,
                    '\n  Status:', response.status,
                    '\n  Status Text:', response.statusText,
                    '\n  Duration:', `${duration}ms`
                  );

                  // 上报到 Sentry（加载失败强制全量上报）
                  safeCaptureExceptionFull(new Error(errorMessage), {
                    tags: {
                      'rsc.url': finalUrl,
                      'rsc.status': response.status,
                      'rsc.statusText': response.statusText,
                      'rsc.duration': duration,
                    },
                    level: 'warning',
                  });
                } else {
                  // 成功时也可以打印日志（可选,用于调试慢请求）
                  if (duration > 3000) {
                    console.warn(
                      '[RscVersionFix] RSC request is slow',
                      '\n  URL:', finalUrl,
                      '\n  Duration:', `${duration}ms`
                    );
                  }
                }

                return response;
              })
              .catch(error => {
                const duration = Date.now() - startTime;

                // 网络错误、超时等
                console.warn(
                  '[RscVersionFix] RSC request error (possibly weak network or disconnected)',
                  '\n  URL:', finalUrl,
                  '\n  Error Type:', error.name,
                  '\n  Error Message:', error.message,
                  '\n  Duration:', `${duration}ms`,
                  '\n  Full Error:', error
                );

                // 上报到 Sentry（网络错误强制全量上报）
                safeCaptureExceptionFull(error, {
                  tags: {
                    'rsc.url': finalUrl,
                    'rsc.errorType': error.name,
                    'rsc.duration': duration,
                  },
                  level: 'warning',
                });

                // 继续抛出错误,保持原有行为
                throw error;
              });
          }
        } catch (e) {
          // 如果 URL 解析失败，使用原始请求
          console.warn('[RscVersionFix] Failed to parse URL:', requestUrl, e);
        }
      }

      return original.call(this, resource, config);
    };

    // 清理函数：组件卸载时恢复原始 fetch
    return () => {
      window.fetch = original;
    };
  }, []);

  return null;
}
